import { PagePhoto } from '.';
import { AdImage, exampleAdImage } from './AdImage';

export type FacebookLeadgenFormType = 'more_volume' | 'higher_intent';

export type FacebookLeadgenBodyStyle = 'LIST_STYLE' | 'PARAGRAPH_STYLE';

export enum FacebookLeadgenButtonType {
  VIEW_WEBSITE = 'VIEW_WEBSITE',
  CALL_BUSINESS = 'CALL_BUSINESS',
  // MESSAGE_BUSINESS = 'MESSAGE_BUSINESS', // not supported as of now
  DOWNLOAD = 'DOWNLOAD',
}

export enum FacebookLeadgenLocale {
  EN_US = 'en_US',
  IT_IT = 'it_IT',
  FR_FR = 'fr_FR',
  ES_ES = 'es_ES',
  ES_LA = 'es_LA',
  DE_DE = 'de_DE',
  EN_GB = 'en_GB',
  PT_BR = 'pt_BR',
  ZH_TW = 'zh_TW',
  ZH_HK = 'zh_HK',
  TR_TR = 'tr_TR',
  AR_AR = 'ar_AR',
  CS_CZ = 'cs_CZ',
  DA_DK = 'da_DK',
  FI_FI = 'fi_FI',
  HE_IL = 'he_IL',
  HI_IN = 'hi_IN',
  HU_HU = 'hu_HU',
  ID_ID = 'id_ID',
  JA_JP = 'ja_JP',
  KO_KR = 'ko_KR',
  NB_NO = 'nb_NO',
  NL_NL = 'nl_NL',
  PL_PL = 'pl_PL',
  PT_PT = 'pt_PT',
  RO_RO = 'ro_RO',
  RU_RU = 'ru_RU',
  SV_SE = 'sv_SE',
  TH_TH = 'th_TH',
  VI_VN = 'vi_VN',
  ZH_CN = 'zh_CN',
}

export type FacebookLeadgenCustomQuestionFormat =
  | 'multiple-choice'
  | 'short-answer';

export type FacebookLeadgenSharingOptions = 'restricted' | 'open';

export enum FacebookLeadgenQuestionType {
  CUSTOM = 'CUSTOM',
  CITY = 'CITY',
  COMPANY_NAME = 'COMPANY_NAME',
  COUNTRY = 'COUNTRY',
  DOB = 'DOB',
  EMAIL = 'EMAIL',
  GENDER = 'GENDER',
  FIRST_NAME = 'FIRST_NAME',
  FULL_NAME = 'FULL_NAME',
  JOB_TITLE = 'JOB_TITLE',
  LAST_NAME = 'LAST_NAME',
  MARITIAL_STATUS = 'MARITIAL_STATUS',
  PHONE = 'PHONE',
  POST_CODE = 'POST_CODE',
  PROVINCE = 'PROVINCE',
  RELATIONSHIP_STATUS = 'RELATIONSHIP_STATUS',
  STATE = 'STATE',
  STREET_ADDRESS = 'STREET_ADDRESS',
  ZIP = 'ZIP',
  WORK_EMAIL = 'WORK_EMAIL',
  MILITARY_STATUS = 'MILITARY_STATUS',
  WORK_PHONE_NUMBER = 'WORK_PHONE_NUMBER',
  STORE_LOOKUP = 'STORE_LOOKUP',
  STORE_LOOKUP_WITH_TYPEAHEAD = 'STORE_LOOKUP_WITH_TYPEAHEAD',
  DATE_TIME = 'DATE_TIME',
  ID_CPF = 'ID_CPF',
  ID_AR_DNI = 'ID_AR_DNI',
  ID_CL_RUT = 'ID_CL_RUT',
  ID_CO_CC = 'ID_CO_CC',
  ID_EC_CI = 'ID_EC_CI',
  ID_PE_DNI = 'ID_PE_DNI',
  ID_MX_RFC = 'ID_MX_RFC',
}

export interface LeadCardQuestionPredefined {
  type: FacebookLeadgenQuestionType;
}

export interface LeadCardQuestionCustom extends LeadCardQuestionPredefined {
  type: FacebookLeadgenQuestionType.CUSTOM;
  format: FacebookLeadgenCustomQuestionFormat;
  label: string;
  key?: string;
  options?: Array<{ key: string; value: string }>; // for multiple choice only
}

export type LeadCardQuestion =
  | LeadCardQuestionPredefined
  | LeadCardQuestionCustom;

export interface LeadCard {
  id: number;
  fbID: string;
  formName: string;
  formType: FacebookLeadgenFormType;
  imageURL?: string;
  imageUsed?: AdImage;
  headline: string; // title of context card
  bodyStyle: FacebookLeadgenBodyStyle;
  bodyItems: string[]; // 1 or many
  questionsDesc?: string; // question_page_custom_headline
  questions: LeadCardQuestion[];
  privacy: {
    link: string;
    text?: string;
    customDisclaimer?: {
      title: string;
      body: string;
      checkboxes: Array<{
        isRequired: boolean;
        isCheckedByDefault: boolean;
        text: string;
      }>;
    };
  };
  thankYouPage?: {
    headline: string;
    description: string;
    callToAction: FacebookLeadgenButtonType;
    buttonText: string;
    link: string;
    phoneNumber?: string;
    countryCode?: string;
  };
  settings: {
    locale: FacebookLeadgenLocale;
    sharing: FacebookLeadgenSharingOptions;
    trackingParams?: { [key: string]: string }; // any key value pairs, multiple
  };
  pagePhoto?: PagePhoto;
}

export const exampleLeadCard: LeadCard = {
  id: 1,
  fbID: '2253452',
  formName: 'New Form',
  formType: 'more_volume',
  imageURL: exampleAdImage.url,
  imageUsed: exampleAdImage,
  headline: 'Headline',
  bodyStyle: 'LIST_STYLE',
  bodyItems: ['Here is my text', 'again again'],
  questionsDesc: 'Please fill out the form',
  questions: [
    { type: FacebookLeadgenQuestionType.CITY },
    {
      type: FacebookLeadgenQuestionType.CUSTOM,
      format: 'multiple-choice',
      options: [
        { key: 'A', value: 'a' },
        { key: 'B', value: 'b' },
      ],
      key: 'a_or_b',
      label: 'Choose a or b',
    },
  ],
  privacy: {
    link: 'url.com',
    text: 'example.com',
    customDisclaimer: {
      title: 'please note',
      body: 'review please',
      checkboxes: [
        { isRequired: true, isCheckedByDefault: true, text: 'i agree' },
      ],
    },
  },
  thankYouPage: {
    headline: 'headline here',
    description: 'thank you so much',
    callToAction: FacebookLeadgenButtonType.VIEW_WEBSITE,
    buttonText: 'click me',
    link: 'link.com',
  },
  settings: {
    locale: FacebookLeadgenLocale.EN_US,
    sharing: 'restricted',
    trackingParams: { foo: 'bar' },
  },
};
